import { convert } from '@revenuemarket/utils';
import dayjs from 'dayjs';
import forge from 'node-forge';

const extractRsaPrivateKey = (pkcs8Pem, password) => {
  pkcs8Pem = addPrivateKeyPEMTag(pkcs8Pem);
  const p8Der = forge.pki.encryptedPrivateKeyFromPem(pkcs8Pem);
  const decryptedPrivateKeyInfo = forge.pki.decryptPrivateKeyInfo(p8Der, password);
  const privateKey = forge.pki.privateKeyFromAsn1(decryptedPrivateKeyInfo);

  return privateKey;
};

const rebuildCertPriP12 = (selected, password) => {
  const privateKey = extractRsaPrivateKey(selected.privateKey, password);
  const p12Asn1 = forge.pkcs12.toPkcs12Asn1(privateKey, selected.certificate, password, {
    algorithm: '3des',
  });

  const p12Der = forge.asn1.toDer(p12Asn1).getBytes();
  const p12b64 = forge.util.encode64(p12Der);

  return p12b64;
};

export const extractP12Certificate = (pkcs12Pfx) => {
  const p12Der = forge.util.binary.base64.decode(pkcs12Pfx);
  const buffer = new forge.util.ByteStringBuffer(p12Der);
  const p12Asn1 = forge.asn1.fromDer(buffer);

  const p12 = forge.pkcs12.certFromP12Asn1(p12Asn1);
  const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
  const certB = certBags[forge.pki.oids.certBag][0];

  return forge.pki.certificateToPem(certB.cert);
};

export const extractP12PrivateCertificate = (pkcs12Pfx, password) => {
  const p12Der = forge.util.binary.base64.decode(pkcs12Pfx);
  const buffer = new forge.util.ByteStringBuffer(p12Der);
  const p12Asn1 = forge.asn1.fromDer(buffer);
  const p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, password);

  const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
  let certB = certBags[forge.pki.oids.certBag][0];
  if (!certB.cert) {
    certB = certBags[forge.pki.oids.certBag][1];
  }

  let priBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag });
  let bag = priBags[forge.pki.oids.pkcs8ShroudedKeyBag][0];
  if (!bag) {
    priBags = p12.getBags({ bagType: forge.pki.oids.keyBag });
    bag = priBags[forge.pki.oids.keyBag][0];
  }
  if (!bag.key) {
    bag = priBags[forge.pki.oids.keyBag][1];
  }
  const privateKey = bag.key;

  const certPem = forge.pki.certificateToPem(certB.cert);
  const priPem = forge.pki.encryptRsaPrivateKey(privateKey, password, {
    legacy: false,
    algorithm: 'seed',
  });
  return {
    privateKey: priPem,
    certificate: certPem,
  };
};

const addCertificatePEMTag = (pemCert) => {
  if (pemCert.indexOf('-----BEGIN CERTIFICATE-----') < 0) {
    return '-----BEGIN CERTIFICATE-----\r\n' + pemCert + '\r\n-----END CERTIFICATE-----';
  }
  return pemCert;
};

const removeCertificatePEMTag = (pemCert) => {
  return pemCert
    .replace('-----BEGIN CERTIFICATE-----', '')
    .replace('-----END CERTIFICATE-----', '');
};

const addPrivateKeyPEMTag = (pemPrivateKey) => {
  if (pemPrivateKey.indexOf('-----BEGIN ENCRYPTED PRIVATE KEY-----') < 0) {
    return (
      '-----BEGIN ENCRYPTED PRIVATE KEY-----\r\n' +
      pemPrivateKey +
      '\r\n-----END ENCRYPTED PRIVATE KEY-----'
    );
  }
  return pemPrivateKey;
};
const removePrivateKeyPEMTag = (pemPrivateKey) => {
  return pemPrivateKey
    .replace('-----BEGIN ENCRYPTED PRIVATE KEY-----', '')
    .replace('-----END ENCRYPTED PRIVATE KEY-----', '');
};

export const parsePEMCertificate = (pemCert) => {
  if (!pemCert) {
    return undefined;
  }

  const certInfo = forge.pki.certificateFromPem(addCertificatePEMTag(pemCert));
  return parseCertificateAttributes(certInfo);
};

const SimpleOidName = {
  '1.2.410.200005.1.1.1': '금융결제원',
  '1.2.410.200005.1.1.4': '금융결제원',
  '1.2.410.200005.1.1.5': '금융결제원',
  '1.2.410.200005.1.1.2': '금융결제원',
  '1.2.410.200005.1.1.6.2': '금융결제원',
  '1.2.410.200005.1.1.6.8': '금융결제원',
  '1.2.410.200005.1.1.6.1': '금융결제원',
  '1.2.410.200005.1.1.8.20': '금융결제원',
  '1.2.410.200005.1.1.4.8': '금융결제원',
  '1.2.410.200005.1.1.4.88': '금융결제원',
};

const parseCertificateAttributes = (certInfo) => {
  const serial = certInfo.serialNumber;
  const subjectCn = forge.util.decodeUtf8(certInfo.subject.getField('CN').value);
  const subjectOu = certInfo.subject.getField('OU').value;
  const idx = subjectCn.indexOf(')');
  const subject = idx > 0 ? subjectCn.substring(0, idx + 1) : subjectCn;
  const simpleSubject = subject;
  const bankCode =
    typeof forge.pki.bankCD !== 'undefined' && idx > 0 && subjectCn.length >= idx + 4
      ? subjectCn.substring(idx + 3, idx + 5)
      : '';

  const issuerO = certInfo.issuer.getField('O').value;
  const issuerCn = certInfo.issuer.getField('CN').value;
  const expireDt = certInfo.validity.notAfter;
  const extension = certInfo.getExtension('certificatePolicies');

  const signAlgName = forge.pki.oids[certInfo.signatureOid];
  const hashAlg = signAlgName.substring(0, signAlgName.indexOf('With'));

  const simpleIssuer = certInfo.issuer.getField('O').value;

  const bankName = forge.pki.bankCD[bankCode];
  const OID = extension.oid;
  const OIDName = convert(
    OID,
    (OID) =>
      (forge.pki.oids[OID] || forge.pki.oids['undefined']) + (bankName ? ' - ' + bankName : ''),
  );

  const isExpired = dayjs().diff(expireDt, 'd') > 0;

  return {
    serial: serial,
    subject: subject,
    subjectCN: subjectCn,
    organizationUnit: subjectOu,
    bank: bankName,
    issuer: issuerO,
    issuerCN: issuerCn,
    expire: dayjs(expireDt).format('YYYY-MM-DD HH:mm:ss'),
    expireDate: dayjs(expireDt).format('YYYY-MM-DD'),
    expireStatus: isExpired ? 'EXPIRED' : 'NORMAL',
    oid: OID,
    oidName: OIDName,
    issuerHash: certInfo.issuer.hash,
    beforeDate: dayjs(certInfo.validity.notBefore).format('YYYY-MM-DD HH:mm:ss'),
    afterDate: dayjs(certInfo.validity.notAfter).format('YYYY-MM-DD HH:mm:ss'),
    hashAlgorithm: hashAlg,
    simpleIssuer: simpleIssuer,
    simpleSubject: simpleSubject,
    simpleOIDName: SimpleOidName[OID] ?? OIDName,
  };
};
